import { Component, Input, OnInit, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { State } from '@k12/forms';
import { EmployeeDetailService, SchoolOption } from '@k12/models';
import { CountiesService, CountyResponse } from '@k12/school-services';
import { SchoolsService } from '@k12/app-services';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';

type TutorSchoolOption = {
  id: string;
  schoolName: string;
  schoolCode?: string;
};

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'lib-tutor-personal-address',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatMomentDateModule,
    NgxMaskDirective
  ],
  providers: [provideNgxMask()],
  templateUrl: './tutor-personal-address.component.html'
})
export class TutorPersonalAddressComponent implements OnInit {
  @Input() formGroup!: FormGroup;
  @Input() states: State[] = [];
  @Input() employeeDetailService?: EmployeeDetailService;
  /** Same as lib-personal-address: partner portal uses Provider Details when false. */
  @Input() showReviewStatus = true;

  private readonly countiesService = inject(CountiesService);
  private readonly schoolsService = inject(SchoolsService);

  counties: CountyResponse[] = [];
  schoolOptions: TutorSchoolOption[] = [];
  isPersonalNorthCarolina = signal(false);
  filteredStates!: Observable<State[]>;
  filteredCounties!: Observable<CountyResponse[]>;
  filteredSchoolOptions!: Observable<TutorSchoolOption[]>;

  ngOnInit(): void {
    this.parseForDatePicker('birthDate');
    this.parseForDatePicker('graduationDate');

    this.filteredStates = this.formGroup.get('personalState')!.valueChanges.pipe(
      startWith(this.formGroup.get('personalState')!.value),
      map((value: string | State | null) => this._filterStates(value || ''))
    );

    this.formGroup
      .get('personalState')!
      .valueChanges.pipe(startWith(this.formGroup.get('personalState')!.value))
      .subscribe((state: string | State | null) => {
        let stateCode: string | undefined;

        if (typeof state === 'object' && state?.code) {
          stateCode = state.code;
        } else if (typeof state === 'string') {
          const matched = this.states.find((s) => s.code === state || s.name === state);
          stateCode = matched?.code;
        }

        this.isPersonalNorthCarolina.set(stateCode === 'NC');

        if (this.isPersonalNorthCarolina()) {
          this.loadCounties('NC');
        } else if (state != null && state !== '') {
          this.formGroup.patchValue({ personalCounty: '' }, { emitEvent: false });
        }
      });

    this.filteredCounties = this.formGroup.get('personalCounty')!.valueChanges.pipe(
      startWith(this.formGroup.get('personalCounty')!.value || ''),
      map((value: string) => this._filterCounties(value || ''))
    );

    this.filteredSchoolOptions = this.formGroup.get('schoolName')!.valueChanges.pipe(
      startWith(this.formGroup.get('schoolName')!.value || ''),
      map((value: string) => this._filterSchoolOptions(value || ''))
    );

    this.formGroup.get('schoolName')!.valueChanges.subscribe((schoolName: string) => {
      this.syncSchoolFromName(schoolName);
    });

    this.loadSchools();
  }

  private _filterStates(value: string | State): State[] {
    const filterValue = typeof value === 'string' ? value.toLowerCase() : value?.name?.toLowerCase() || '';
    return this.states.filter((s) => s.name.toLowerCase().includes(filterValue));
  }

  private _filterCounties(value: string): CountyResponse[] {
    const filterValue = value.toLowerCase();
    return this.counties.filter((county) => county.name.toLowerCase().includes(filterValue));
  }

  private _filterSchoolOptions(value: string): TutorSchoolOption[] {
    const filterValue = String(value).toLowerCase();
    return this.schoolOptions.filter((school) => {
      const label = this.formatSchoolLabel(school).toLowerCase();
      return label.includes(filterValue);
    });
  }

  formatSchoolLabel(school: TutorSchoolOption): string {
    return school.schoolCode ? `${school.schoolCode} - ${school.schoolName}` : school.schoolName;
  }

  onSchoolSelected(event: MatAutocompleteSelectedEvent): void {
    const school = event.option.value as TutorSchoolOption;
    this.formGroup.patchValue(
      {
        schoolName: school.schoolName,
        schoolId: school.id,
        schoolCode: school.schoolCode ?? ''
      },
      { emitEvent: false }
    );
  }

  displayStateName(state: State | null): string {
    return state?.name || '';
  }

  private loadCounties(state: string): void {
    this.countiesService.getCountiesByState(state).subscribe({
      next: (data: CountyResponse[]) => {
        this.counties = data;
        this.formGroup.get('personalCounty')!.updateValueAndValidity({ emitEvent: true });
      },
      error: () => {
        this.counties = [];
      }
    });
  }

  private loadSchools(): void {
    if (this.employeeDetailService) {
      this.employeeDetailService.getSchools().subscribe({
        next: (schools) => {
          console.log('schools', schools);
          this.schoolOptions = (schools ?? [])
            .filter((school) => (school.schoolName ?? '').trim() !== '')
            .map((school) => ({
              id: String(school.id),
              schoolName: school.schoolName,
              schoolCode: school.schoolCode
            }));
          this.syncSchoolFromName(this.formGroup.get('schoolName')!.value || '');
          this.formGroup.get('schoolName')!.updateValueAndValidity({ emitEvent: true });
        },
        error: () => {
          this.schoolOptions = [];
        }
      });
      return;
    }

    this.schoolsService.getSchoolsForDropdown('', true).subscribe({
      next: (res) => {
        const source: SchoolOption[] = Array.isArray(res?.entity) ? res.entity : [];
        this.schoolOptions = source
          .filter((school) => (school.name ?? '').trim() !== '')
          .map((school) => ({
            id: school.id,
            schoolName: school.name,
            schoolCode: school.schoolCode
          }));
        this.syncSchoolFromName(this.formGroup.get('schoolName')!.value || '');
        this.formGroup.get('schoolName')!.updateValueAndValidity({ emitEvent: true });
      },
      error: () => {
        this.schoolOptions = [];
      }
    });
  }

  private syncSchoolFromName(schoolName: string): void {
    const school = this.schoolOptions.find((option) => option.schoolName === schoolName);
    this.formGroup.patchValue(
      {
        schoolId: school?.id ?? '',
        schoolCode: school?.schoolCode ?? ''
      },
      { emitEvent: false }
    );
  }

  /** Form is patched as MM/DD/YYYY by user-info-panel; convert to Date for the picker. */
  private parseForDatePicker(controlName: string): void {
    const control = this.formGroup.get(controlName);
    const rawValue = control?.value;

    if (!control || rawValue == null || rawValue === '' || rawValue instanceof Date) {
      return;
    }

    const [month, day, year] = String(rawValue).trim().split('/').map(Number);
    if (!month || !day || !year) return;

    control.setValue(new Date(year, month - 1, day), { emitEvent: false });
  }
}
